// frontend/src/pages/admin/Settings.js
import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from "@mui/material";
import { adminAPI } from "../../services/api";

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    minCreditPurchase: 10,
    maxCreditPurchase: 1000,
    lowBalanceThreshold: 50,
    autoSuspendThreshold: 0,
    enableAutoSuspend: false,
    webhookUrl: "",
    webhookSecret: "",
    emailNotifications: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      await adminAPI.updateSettings(settings);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        System Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings updated successfully!
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Minimum Credit Purchase"
              type="number"
              value={settings.minCreditPurchase}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  minCreditPurchase: Number(e.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Maximum Credit Purchase"
              type="number"
              value={settings.maxCreditPurchase}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  maxCreditPurchase: Number(e.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Low Balance Threshold"
              type="number"
              value={settings.lowBalanceThreshold}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  lowBalanceThreshold: Number(e.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Auto-Suspend Threshold"
              type="number"
              value={settings.autoSuspendThreshold}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  autoSuspendThreshold: Number(e.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enableAutoSuspend}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      enableAutoSuspend: e.target.checked,
                    })
                  }
                />
              }
              label="Enable Auto-Suspend"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Webhook URL"
              value={settings.webhookUrl}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  webhookUrl: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Webhook Secret"
              type="password"
              value={settings.webhookSecret}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  webhookSecret: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.emailNotifications}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      emailNotifications: e.target.checked,
                    })
                  }
                />
              }
              label="Enable Email Notifications"
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={handleSave} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Save Settings"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Email Settings Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SMTP Host"
              value={settings.smtpHost}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  smtpHost: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SMTP Port"
              type="number"
              value={settings.smtpPort}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  smtpPort: Number(e.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SMTP Username"
              value={settings.smtpUsername}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  smtpUsername: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SMTP Password"
              type="password"
              value={settings.smtpPassword}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  smtpPassword: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Sender Email Address"
              value={settings.senderEmail}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  senderEmail: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>
      </Paper>

      {/* API Integration Settings */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          API Integration Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="ESIM Provider API URL"
              value={settings.esimProviderApiUrl}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  esimProviderApiUrl: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="API Key"
              value={settings.apiKey}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  apiKey: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="API Secret"
              type="password"
              value={settings.apiSecret}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  apiSecret: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>
      </Paper>

      {/* System Maintenance */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          System Maintenance
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.maintenanceMode}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      maintenanceMode: e.target.checked,
                    })
                  }
                />
              }
              label="Maintenance Mode"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Maintenance Message"
              value={settings.maintenanceMessage}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  maintenanceMessage: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          onClick={() => window.location.reload()}
          disabled={loading}
        >
          Reset
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Save All Settings"}
        </Button>
      </Box>
    </Box>
  );
};

export default AdminSettings;
